import React, {useCallback, useState} from 'react';
import {AccentColorOptions, useBreakpointUp, useIsTwinmotionTheme} from 'epic-ue-theme';
import styled from 'styled-components';
import {Typography} from '../../typography';
import {ClickAwayListener} from '@material-ui/core';
import {CarretIcon} from './CarretIcon';
import root from 'window-or-global';

export interface CustomDropdownProps {
    accentColor?: AccentColorOptions;
    label?: string;
    children: React.ReactNode;
    hasCheckedItems?: boolean;
}

const Styled = styled.div<{accentColor: AccentColorOptions}>`
    cursor: pointer;
    user-select: none;
    outline: none;
    color: ${(p) => p.theme.palette.common.black};

    & > div:focus {
        .select-label {
            outline: none;
        }
    }

    .select-label {
        background-color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.common.white, p.theme.palette.background.paper)};
        color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.common.black, p.theme.palette.common.white)};
        width: fit-content;
        padding: 10px 15px 10px 15px;
        font-size: 15px;
        font-weight: 600;
        letter-spacing: 2px;
        &:focus {
            outline: none;
        }
    }
    .select-dropdown {
        background-color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.common.white, p.theme.palette.background.paper)};
        span {
            color: ${(p) =>
                p.theme.ifLight(p, p.theme.palette.common.black, p.theme.palette.common.white)};
        }
        svg {
            height: 30px;
            min-height: 30px;
        }
        position: absolute;
        display: none;
    }

    .opened .select-dropdown {
        display: block;
        ${(p) => p.theme.breakpoints.down('sm')} {
            padding: 2px;
        }
    }

    .select-arrow {
        float: right;
    }

    .opened .select-arrow {
        float: right;
    }
    .has-checked-items {
        .select-label {
            border-bottom: 3px solid #34b3fa;
        }
    }

    .select-label h5 {
        text-transform: unset;
    }
`;

export const CustomDropdown = ({
    children,
    label = '',
    accentColor = 'sky',
    hasCheckedItems
}: CustomDropdownProps): JSX.Element => {
    const [isOpen, setIsOpen] = useState(false);
    const isMd = useBreakpointUp('md');

    const isTwinmotion = useIsTwinmotionTheme();

    const toggleOpen = useCallback(() => {
        isOpen ? setIsOpen(false) : setIsOpen(true);
    }, [setIsOpen, isOpen]);

    const handleBlur = (e) => {
        const currentTarget = e.currentTarget;

        // Check the newly focused element in the next tick of the event loop
        isOpen &&
            setTimeout(() => {
                // Check if the new activeElement is a child of the original container
                if (!currentTarget.contains(root.document.activeElement)) {
                    // You can invoke a callback or add custom logic here
                    setIsOpen(false);
                }
            }, 0);
    };

    return (
        <Styled
            className="selectCustom"
            accentColor={accentColor}
            onBlur={(e) => {
                handleBlur(e);
            }}>
            <ClickAwayListener
                onClickAway={() => {
                    isMd && setIsOpen(false);
                }}>
                <div
                    className={`${isOpen ? 'opened' : ''} ${
                        hasCheckedItems ? 'has-checked-items' : ''
                    }`}>
                    <div
                        role="menuitem"
                        tabIndex={0}
                        className="select-label"
                        onFocus={() => {
                            !isOpen && setIsOpen(true);
                        }}
                        onMouseDown={toggleOpen}>
                        <Typography
                            className={!isTwinmotion ? 'less-bold' : ''}
                            variant={isTwinmotion ? 'h5' : 'p'}>
                            {label} &nbsp;
                            <span className="select-arrow">
                                <CarretIcon isOpen={isOpen} />
                            </span>
                        </Typography>
                    </div>
                    <div className="select-dropdown">{children}</div>
                </div>
            </ClickAwayListener>
        </Styled>
    );
};
